import LogoSpinner from '@/components/atoms/LogoSpinner';
import SidebarItemViewTemplate from '@/components/templates/SidebarItemViewTemplate';
import useAdminFeatureFlags from '@/hooks/feature-flags/useAdminFeatureFlags';
import FeatureFlagConfirmModal from '@/modules/super-admin/FeatureFlagConfirmModal';
import { TFeatureFlagKey } from '@/services/api/requests/featureFlags';
import { HStack, StackDivider, Switch, Text, VStack } from '@chakra-ui/react';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

// Human-friendly copy per known flag; unknown keys fall back to a humanized label.
const FLAG_META: Partial<Record<TFeatureFlagKey, { label: string; description: string }>> = {
  'content-creator': {
    label: 'Content Creator',
    description: 'Enables the content creator application flow and creator-authored content across the apps.',
  },
};

const humanize = (key: string): string =>
  key
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const FeaturesManagementPage = () => {
  const { flags, isLoading, isError, setFlag, isUpdating } = useAdminFeatureFlags();
  // The pending toggle awaiting confirmation (null = no modal open).
  const [pending, setPending] = useState<{ key: TFeatureFlagKey; label: string; enabled: boolean } | null>(null);

  const confirmToggle = () => {
    if (!pending) return;
    setFlag({ key: pending.key, enabled: pending.enabled }, { onSuccess: () => setPending(null) });
  };

  return (
    <SidebarItemViewTemplate title="Features Management">
      <VStack padding={'40px'} width={'100%'} alignItems={'flex-start'}>
        <VStack
          background={'white'}
          borderRadius={'20px'}
          boxShadow={'0px 4px 20px rgba(0, 0, 0, 0.1)'}
          padding={'40px'}
          alignItems={'stretch'}
          maxWidth={'900px'}
          width={'100%'}
          gap={'24px'}
        >
          <VStack alignItems={'flex-start'} gap={'6px'}>
            <Text variant={'urbanistSemiBold'} color={'text.darkBlue'} fontSize={'26px'}>
              Product Feature Flags
            </Text>
            <Text color={'text.mediumGray'}>
              Toggle product features on or off across the platform. Changes apply immediately for all users.
            </Text>
          </VStack>

          {isLoading ? (
            <HStack justifyContent={'center'} paddingY={'40px'} width={'100%'}>
              <LogoSpinner width="48px" />
            </HStack>
          ) : isError ? (
            <Text color={'extra.red'}>Couldn’t load feature flags. Please try again.</Text>
          ) : flags.length === 0 ? (
            <Text color={'text.mediumGray'}>No feature flags available.</Text>
          ) : (
            <VStack alignItems={'stretch'} gap={0} divider={<StackDivider borderColor={'background.separatorBlue'} />}>
              {flags.map((flag) => {
                const meta = FLAG_META[flag.key];
                return (
                  <HStack key={flag.key} justifyContent={'space-between'} alignItems={'center'} gap={'24px'} paddingY={'18px'}>
                    <VStack alignItems={'flex-start'} gap={'2px'}>
                      <Text variant={'urbanistSemiBold'} color={'text.darkBlue'} fontSize={'16px'}>
                        {meta?.label ?? humanize(flag.key)}
                      </Text>
                      {meta?.description && (
                        <Text color={'text.mediumGray'} fontSize={'14px'} maxWidth={'560px'}>
                          {meta.description}
                        </Text>
                      )}
                    </VStack>
                    <Switch
                      size={'lg'}
                      colorScheme={'purple'}
                      isChecked={flag.enabled}
                      isDisabled={isUpdating}
                      onChange={(e) =>
                        setPending({ key: flag.key, label: meta?.label ?? humanize(flag.key), enabled: e.target.checked })
                      }
                    />
                  </HStack>
                );
              })}
            </VStack>
          )}
        </VStack>
      </VStack>

      <FeatureFlagConfirmModal
        isOpen={pending !== null}
        onClose={() => setPending(null)}
        featureLabel={pending?.label ?? ''}
        enabling={pending?.enabled ?? false}
        isLoading={isUpdating}
        onConfirm={confirmToggle}
      />
    </SidebarItemViewTemplate>
  );
};

export const Route = createFileRoute('/_authenticated/_admin/super-admin/features-management')({
  component: FeaturesManagementPage,
});
